import {useCallback} from 'react';

import {observer} from 'mobx-react-lite';

import {i18n} from '@yourcoach/i18n';

import type {GetComponentProps} from '../../../../utils/utility-types';
import type {RefundsListItem} from '../RefundsListItem';
import type {Props} from './types';

import {getCardBrandImage} from '../../../../utils/entity/card/getCardBrandImage';
import * as S from './styles';

export const PaymentInfoContainer = observer<Props>(
  ({onRetryRefundButtonClick, store}) => {
    const onRetryRefundButtonClickCb: GetComponentProps<
      typeof RefundsListItem
    >['onRetryButtonClick'] = useCallback(
      (_e, refund) => {
        onRetryRefundButtonClick(refund);
      },
      [onRetryRefundButtonClick],
    );

    const renderClientName = () =>
      store.payment?.transferToMe ? (
        <S.Row
          title={i18n.t('shared:client_name')}
          value={store.payment.userName}
        />
      ) : null;

    const renderTotalPaid = () => (
      <S.Row
        title={i18n.t('shared:total_paid')}
        value={store.payment!.totalAmountString}
        variant="highlighted"
      />
    );

    const renderTransferAmount = () => (
      <>
        <S.Row
          title={i18n.t('shared:stripe_commission')}
          value={store.payment!.taxString}
          variant="highlighted"
        />
        <S.Row
          title={i18n.t('shared:total_received')}
          value={store.payment!.transferToMeString}
          variant="highlighted"
        />
      </>
    );

    const renderRefundAmount = () =>
      store.payment?.refundedAmount ? (
        <S.Row
          title={i18n.t('shared:refunded')}
          value={store.payment?.refundedAmountString}
          variant="highlighted"
        />
      ) : null;

    const renderAmount = () =>
      store.payment?.transferToMe
        ? renderTransferAmount()
        : renderRefundAmount();

    const renderCard = () =>
      store.payment?.paymentMethod ? (
        <S.Row
          title={i18n.t('shared:billed_to')}
          value={
            <S.PaymentMethodContainer>
              <S.CardContent>
                <S.CardBrand
                  resizeMode="contain"
                  src={getCardBrandImage(
                    store.payment.paymentMethod.card!.brand,
                  )}
                />
                <S.CardLastDigits>
                  {`**** ${store.payment.paymentMethod.card!.last4}`}
                </S.CardLastDigits>
              </S.CardContent>
            </S.PaymentMethodContainer>
          }
        />
      ) : null;

    const renderDate = () => (
      <S.Row
        title={i18n.t('shared:payment_date')}
        value={store.payment!.dateString}
      />
    );

    const renderCourseInfo = () =>
      store.payment?.course ? (
        <>
          <S.Row
            title={i18n.t('shared:program_label')}
            value={store.payment.programTitle}
          />
          <S.Row
            title={i18n.t('shared:dates')}
            value={store.payment.courseLabel}
          />
        </>
      ) : null;

    const renderRefundsInfo = () =>
      store.payment?.shouldShowRefunds ? (
        <S.RefundsContainer>
          <S.RefundsHeaderContainer>
            {store.payment.hasRefunds ? (
              <S.RefundsHeader>{i18n.t('shared:refunds')}</S.RefundsHeader>
            ) : null}
            {store.payment.isPartiallyRefunded ? (
              <S.RefundsStatusContainer>
                <S.RefundsStatus>{i18n.t('shared:partial')}</S.RefundsStatus>
              </S.RefundsStatusContainer>
            ) : null}
          </S.RefundsHeaderContainer>
          {store.payment?.refunds.map(refund => (
            <S.RefundsListItem
              key={refund._id}
              onRetryButtonClick={onRetryRefundButtonClickCb}
              payment={store.payment!.toJS}
              refund={refund}
            />
          ))}
        </S.RefundsContainer>
      ) : null;

    const renderSkeleton = () => (
      <>
        <S.RowSkeleton />
        <S.RowSkeleton />
        <S.RowSkeleton />
        <S.RowSkeleton />
        <S.RowSkeleton />
        <S.RowSkeleton />
        <S.RowSkeleton />
      </>
    );

    return (
      <S.Container>
        {store.initialLoad.isDone ? (
          <>
            {renderClientName()}
            {renderTotalPaid()}
            {renderAmount()}
            {renderCard()}
            {renderDate()}
            {renderCourseInfo()}
            {renderRefundsInfo()}
          </>
        ) : (
          renderSkeleton()
        )}
      </S.Container>
    );
  },
);
